import * as Y from 'yjs';
import type { NodeType } from '../../../utils/node-types';
import { defaultDataForType } from '@/features/canvas/engine/schema/defaults';
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from '@/features/canvas/engine/wiring/canvas-roots';

/**
 * Canonical helper for creating a node Y.Map on a Canvas Y.Doc.
 *
 * Converts string fields that are supposed to be Y.Text (sticky.text,
 * text.text, shape.text — when supported) into actual Y.Text instances
 * inside a single doc.transact, attached to the host Y.Doc. Without this,
 * any caller using `defaultDataForType('sticky')` would ship a node whose
 * renderer crashes with `text.observe is not a function` on first render.
 *
 * The node is inserted into `doc.getMap('nodes')` within the same transaction
 * so Y.Text fields are properly integrated before `insert()` is called — Yjs
 * requires a type to be part of the document tree before you can write to it.
 *
 * Use this from the playground, future toolbar, paste fallback, AI emission,
 * and any GC repair path. NEVER construct node Y.Maps inline.
 *
 * Returns the newly created node Y.Map (already in doc.getMap('nodes')).
 */
export function createNode(
  doc: Y.Doc,
  type: NodeType,
  position: { x: number; y: number },
  overrides?: Partial<Record<string, unknown>>,
  origin?: unknown,
  size?: { width: number; height: number } | null,
  binding: CanvasBinding = TOP_LEVEL_BINDING,
): Y.Map<unknown> {
  const id = crypto.randomUUID();
  const node = new Y.Map<unknown>();
  const dataMap = new Y.Map<unknown>();
  const now = Date.now();

  // Build defaults; merge overrides; convert text fields to Y.Text
  const defaults = defaultDataForType(type);
  const data = { ...defaults, ...overrides };

  doc.transact(() => {
    const { nodes: nodesMap, zOrder } = canvasRoots(doc, binding);

    // Integrate node and dataMap into the doc tree FIRST — required before any
    // Y.Text.insert() calls (Yjs: "Add Yjs type to a document before reading data")
    node.set('id', id);
    node.set('type', type);
    node.set('position', position);
    node.set('size', size ?? null);
    node.set('parentId', null);
    node.set('rotation', 0);
    node.set('locked', false);
    node.set('createdAt', now);
    node.set('createdBy', '');  // caller can override post-create
    node.set('updatedAt', now);
    node.set('data', dataMap);
    nodesMap.set(id, node);  // integrate into doc tree
    zOrder.push([id]);

    // Now populate dataMap — Y.Text / Y.Array are safe to integrate after
    // the parent maps are in the doc tree.
    for (const [key, value] of Object.entries(data)) {
      if (isTextField(type, key)) {
        const ytext = new Y.Text();
        dataMap.set(key, ytext);
        const initialContent = typeof value === 'string' ? value : '';
        if (initialContent.length > 0) ytext.insert(0, initialContent);
      } else if (isYArrayField(type, key)) {
        // Frame.childOrder must be a Y.Array so concurrent reorders /
        // inserts converge correctly (useAutoAttach pushes to it,
        // useFrameDelete calls toArray() on it). The default from
        // Frame/defaults.ts is a plain [] for type ergonomics — we
        // convert to Y.Array here at creation time so the runtime payload
        // has the right CRDT type.
        const yarr = new Y.Array<string>();
        dataMap.set(key, yarr);
        if (Array.isArray(value) && value.length > 0) {
          yarr.push(value as string[]);
        }
      } else {
        dataMap.set(key, value);
      }
    }
  }, origin);

  return node;
}

/** Node types whose `text` field is a collaborative Y.Text. */
const TEXT_FIELD_TYPES = new Set<NodeType>(['sticky', 'text', 'shape']);

function isTextField(type: NodeType, key: string): boolean {
  return key === 'text' && TEXT_FIELD_TYPES.has(type);
}

/** Fields that must be stored as Y.Array (not plain JS array) because
 *  collaborative writers (push, delete, observe) target the CRDT type. */
function isYArrayField(type: NodeType, key: string): boolean {
  return type === 'frame' && key === 'childOrder';
}
